'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import retry from 'async-retry';
import { Buffer } from 'buffer';
import { GraphQLClient } from 'graphql-request';
import getCookieDomain from '@repo/utils/getCookieDomain';
import { pushToDataLayer } from '@repo/utils';
import { useUiComponentsInternationalContext } from '@repo/static-translations';
import { LocaleCurrencyEnum } from '@repo/models/LocaleCurrencyEnum';
import {
  isPixelSupported,
  pixelCategoryFromLocation,
  PIXELS,
  waitForKlaviyo,
} from '@repo/utils/pixels';
import { readBrowserStorage, writeBrowserStorage } from './storageUtil';
import {
  ADD_LINE_ITEM_QUERY,
  CREATE_CART_QUERY,
  GET_CART_QUERY,
  REMOVE_LINE_ITEM_QUERY,
  UPDATE_CART_ATTRIBUTES_QUERY,
  UPDATE_LINE_ITEM_QUERY,
} from './cartQueries';
import { LocaleContext } from './LocaleContext';

interface ShopifySettings {
  cartIdKey: string;
  domainName?: string;
  accessToken?: string;
}

interface LineItemInput {
  id?: string;
  quantity?: number;
  merchandiseId?: string;
  attributes?: Array<{ key: string; value: string }>;
  sellingPlanId?: string;
}

interface CartLineItem {
  id: string;
  quantity: number;
  merchandise: {
    id: string;
    price?: {
      amount?: number;
      currencyCode?: string;
    };
    product: any;
    sku?: string;
    compareAtPrice?: string;
    image?: {
      src?: string;
    };
  };
  sellingPlanAllocation?: {
    priceAdjustments?: Array<{
      price?: {
        amount?: number;
      };
    }>;
    sellingPlan?: {
      id?: string;
    };
  };
  attributes: Array<{ key: string; value: string }>;
}

interface Cart {
  id: string;
  lines: {
    edges: Array<{
      node: CartLineItem;
    }>;
  };
  cost?: {
    subtotalAmount?: {
      currencyCode?: string;
      amount?: number;
    };
  };
  totalQuantity?: number;
  subtotalAmount?: {
    currencyCode?: string;
    amount?: number;
  };
  checkoutUrl?: string;
  attributes?: Array<{ key: string; value: string }>;
}

interface CartProviderProps {
  children: React.ReactNode;
  shopifySettings?: ShopifySettings;
}

interface ToastAddOnsValue {
  imageUrl?: string;
  title?: string;
  price?: number;
  sku?: string;
  cartProps?: {
    variantId?: string;
    quantity?: string;
  };
}

interface AddToCartArgs {
  activeVariant: {
    shopifyId: string;
    sku: string;
    price: number;
    compareAtPrice?: number;
    media?: Array<{ image?: { src: string } }>;
  };
  addOns?: ToastAddOnsValue[];
  product: {
    shopifyId: string;
    title: string;
    vendor: string;
    shopifyProduct?: {
      shopifyId: string;
      title: string;
      vendor: string;
    };
    store?: { previewImageUrl?: string };
  };
  quantity: number;
  selectedSellingPlan?: {
    id: string;
    name?: string;
    percentageDiscount?: number;
  };
  sellingPlanGroups?: Array<{
    sellingPlans?: {
      nodes: Array<{
        id: string;
        name: string;
      }>;
    };
  }>;
  variantId: string;
  variantMetafields?: Array<{ name?: string; key?: string; value: string }>;
}

interface CartContextValue {
  addToCart: (args: AddToCartArgs) => Promise<void>;
  updateLineItems: (args: LineItemInput[]) => Promise<void>;
  incrementLineItem: (lineItemId: string) => Promise<void>;
  decrementLineItem: (lineItemId: string) => Promise<void>;
  emptyCart: () => Promise<void>;
  deleteLineItem: (lineItemId: string) => Promise<void>;
  setCartActive: (state: boolean) => void;
  hideToastNotification: () => void;
  hideToast: () => void;
  cart: Cart;
  cartActive: boolean;
  cartBusy: boolean;
  cartError: boolean;
  showToast: boolean;
  toastProduct?: CartLineItem;
  toastAddOns?: ToastAddOnsValue[];
  updateCartAttributes: (
    attributes: Array<{ key: string; value: string }>
  ) => Promise<void>;
  shopifyClient: GraphQLClient;
  formatPrice: (price: number) => string;
}

const defaultState: CartContextValue = {
  addToCart: async () => {},
  updateLineItems: async () => {},
  incrementLineItem: async () => {},
  decrementLineItem: async () => {},
  emptyCart: async () => {},
  deleteLineItem: async () => {},
  setCartActive: () => {},
  hideToastNotification: () => {},
  hideToast: () => {},
  cart: {
    id: '',
    lines: {
      edges: [],
    },
  },
  cartActive: false,
  cartBusy: false,
  cartError: false,
  showToast: false,
  toastProduct: null,
  toastAddOns: [],
  updateCartAttributes: async () => {},
  shopifyClient: new GraphQLClient(''),
  formatPrice: () => '',
};

const formatCadPrice = (price: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'CAD',
  }).format(price);

const CAD_CART_KEY_IDENTIFIER = '_cadCartId';
const SUBSCRIPTION_FREQUENCY_METAFIELD = 'subscription_frequency';
const CART_API_VERSION = '2025-04';

const CartContext = createContext(defaultState);

const CartProvider: React.FC<CartProviderProps> = ({
  children,
  shopifySettings = {
    cartIdKey: '_cartId',
    domainName: process.env.GATSBY_SHOPIFY_DOMAIN_NAME,
    accessToken: process.env.GATSBY_SHOPIFY_ACCESS_TOKEN,
  },
}) => {
  const localeContext = useContext(LocaleContext);
  const { localizePrice } = useUiComponentsInternationalContext();
  const currency = localeContext?.currency ?? LocaleCurrencyEnum.USD;

  const [cart, setCart] = useState<Cart>(defaultState.cart);
  const [cartActive, setCartActive] = useState<boolean>(
    defaultState.cartActive
  );
  const [cartBusy, setCartBusy] = useState<boolean>(defaultState.cartBusy);
  const [cartError, setCartError] = useState<boolean>(defaultState.cartError);
  const [showToast, setShowToast] = useState<boolean>(defaultState.showToast);
  const [toastProduct, setToastProduct] = useState<CartLineItem>(
    defaultState.toastProduct
  );
  const [toastAddOns, setToastAddOns] = useState<ToastAddOnsValue[]>(
    defaultState.toastAddOns
  );

  const shopifySettingsForCurrency = useMemo(() => {
    if (currency === LocaleCurrencyEnum.CAD) {
      return {
        domainName: process.env.GATSBY_SHOPIFY_CAD_DOMAIN_NAME,
        accessToken: process.env.GATSBY_SHOPIFY_CAD_ACCESS_TOKEN,
        cartIdKey: CAD_CART_KEY_IDENTIFIER,
      };
    }
    return shopifySettings;
  }, [currency, shopifySettings]);

  const [shopifyClient, setShopifyClient] = useState(
    new GraphQLClient(
      `https://${shopifySettingsForCurrency.domainName}/api/${CART_API_VERSION}/graphql.json`,
      {
        headers: {
          'X-Shopify-Storefront-Access-Token':
            shopifySettingsForCurrency.accessToken,
        },
      }
    )
  );

  const setCartActiveHandler = (activeState: boolean) => {
    setCartActive(activeState);
    setShowToast(false);
  };

  const createNewCart = async (client: GraphQLClient) => {
    try {
      const newCart = await client.request(CREATE_CART_QUERY, {
        input: {
          attributes: process.env.GATSBY_RSC_ENV
            ? [
                {
                  key: 'rscEnv',
                  value: process.env.GATSBY_RSC_ENV,
                },
              ]
            : [],
          buyerIdentity: {
            countryCode: localeContext?.getCountryCode() ?? 'US',
          },
        },
      });
      writeBrowserStorage(
        shopifySettingsForCurrency.cartIdKey,
        newCart?.cartCreate?.cart?.id ?? '',
        getCookieDomain()
      );
      return newCart?.cartCreate?.cart;
    } catch (error) {
      console.error('createNewCart error:', error);
    }

    console.error('Something went wrong when trying to create new cart.');
    return null;
  };

  const initializeCart = async (
    client: GraphQLClient,
    shopifySettingsArg: ShopifySettings
  ) => {
    const cartId = readBrowserStorage(shopifySettingsArg.cartIdKey);
    if (cartId) {
      const existingCart = await client
        .request(GET_CART_QUERY, {
          id: cartId,
        })
        .catch(() => {
          // Couldn't retrieve cart based on cookie/localStorage _cartId
          // so create a fresh one
          console.warn(
            'Could not retrieve cart based on stored ID, creating a new cart.'
          );
          return createNewCart(client);
        });

      // Is existing cart stale?
      if (!existingCart?.cart) {
        console.warn(
          'Fetch cart call returned null value (stale cart). Creating new cart.'
        );
        return createNewCart(client);
      }

      return existingCart.cart;
    }

    console.info(
      'Could not retrieve cart based on stored ID, created a new cart'
    );
    return createNewCart(client);
  };

  const updateCart = async (newCart: Cart) => {
    if (
      typeof newCart !== 'undefined' &&
      typeof newCart === 'object' &&
      Object.prototype.hasOwnProperty.call(newCart, 'id')
    ) {
      setCart(newCart);
    }
  };

  const updateLineItems = async (lineItemsToUpdate: LineItemInput[]) => {
    setCartBusy(true);

    try {
      const newCartUpdateLine = await shopifyClient.request(
        UPDATE_LINE_ITEM_QUERY,
        {
          cartId: cart.id,
          lines: lineItemsToUpdate,
        }
      );

      await updateCart(newCartUpdateLine.cartLinesUpdate.cart);

      setCart(newCartUpdateLine.cartLinesUpdate.cart);
      setCartBusy(false);
    } catch (error) {
      console.error('updateLineItem error:', lineItemsToUpdate, error);
      setCartBusy(false);
    }
  };

  /**
   * Adds product to cart. Repeated adds increment quantity.
   */
  const addToCart = async ({
    activeVariant,
    addOns,
    product,
    quantity,
    selectedSellingPlan,
    sellingPlanGroups,
    variantId,
    variantMetafields,
  }: AddToCartArgs) => {
    setCartBusy(true);

    try {
      const lineItem: LineItemInput = {
        quantity,
        merchandiseId: Buffer.from(variantId).toString('base64'),
        attributes: [],
      };

      // IFB subscriptions
      const productDeliveryFrequency = variantMetafields?.find(
        metafield =>
          metafield.name === SUBSCRIPTION_FREQUENCY_METAFIELD ||
          metafield.key === SUBSCRIPTION_FREQUENCY_METAFIELD
      )?.value;

      if (productDeliveryFrequency) {
        // Turn complex graphql structure into {id: string, name: string}[]
        // This supports both pre/post recharge upgrade
        const simplifiedSellingPlans = sellingPlanGroups?.reduce(
          (prev, item) => {
            if (item.sellingPlans?.nodes) {
              prev.push(...item.sellingPlans.nodes);
            }
            return prev;
          },
          []
        );

        const planTemplates = [
          `delivery every ${productDeliveryFrequency} months`,
          `delivery every ${productDeliveryFrequency} month`,
          `delivery every ${productDeliveryFrequency} days`,
          `delivery every ${productDeliveryFrequency} day`,
          `auto-renews every ${productDeliveryFrequency} months`,
          `auto-renews every ${productDeliveryFrequency} month`,
          `auto-renews every ${productDeliveryFrequency} days`,
          `auto-renews every ${productDeliveryFrequency} day`,
          `${productDeliveryFrequency} month subscription`,
          `${productDeliveryFrequency} months subscription`,
          `${productDeliveryFrequency} day subscription`,
          `${productDeliveryFrequency} days subscription`,
        ];

        const sellingPlan = simplifiedSellingPlans.find(node =>
          planTemplates.includes(node.name.toLowerCase())
        );

        const sellingPlanId = sellingPlan?.id;

        if (sellingPlanId) {
          lineItem.sellingPlanId =
            Buffer.from(sellingPlanId).toString('base64');
        }
      }

      // PetSafe subscriptions
      if (selectedSellingPlan?.id) {
        lineItem.sellingPlanId = Buffer.from(selectedSellingPlan.id).toString(
          'base64'
        );
        if (selectedSellingPlan.name) {
          lineItem.attributes.push({
            key: 'Auto-renews',
            value: selectedSellingPlan.name,
          });
        }

        if (selectedSellingPlan.percentageDiscount) {
          lineItem.attributes.push({
            key: 'Subscription Discount Applied',
            value: `${selectedSellingPlan.percentageDiscount}%`,
          });
        }
      }

      const cartId = readBrowserStorage(shopifySettingsForCurrency.cartIdKey);

      setToastAddOns([]);
      let addOnLineItems = [];
      if (addOns?.length > 0) {
        addOnLineItems = addOns.map(({ cartProps }) => ({
          quantity: 1,
          merchandiseId: Buffer.from(cartProps.variantId).toString('base64'),
          attributes: [],
        }));
        setToastAddOns(addOns);
      }

      const newCartAddLine = await shopifyClient.request(ADD_LINE_ITEM_QUERY, {
        cartId,
        lines: [lineItem, ...addOnLineItems],
      });

      let addedLineItem = newCartAddLine.cartLinesAdd.cart?.lines.edges.find(
        ({ node }) =>
          node.merchandise.id === variantId &&
          node?.sellingPlanAllocation?.sellingPlan?.id ===
            selectedSellingPlan?.id
      );

      // Try to find line item without selectedSellingPlan
      addedLineItem ??= newCartAddLine.cartLinesAdd.cart?.lines.edges.find(
        ({ node }) => node.merchandise.id === variantId
      );

      await updateCart(newCartAddLine.cartLinesAdd.cart);

      const discounts = addedLineItem.node.discountAllocations ?? [];

      // Add a line item for applied promotions, if they exist...KA
      if (discounts.length) {
        await updateLineItems([
          {
            id: addedLineItem.node.id,
            attributes: [
              ...addedLineItem.node.attributes,
              {
                key: 'Promotions Applied',
                value: discounts.map(d => d.title).join(', '),
              },
            ],
          },
        ]);
      }

      setCart(newCartAddLine.cartLinesAdd.cart);

      setCartBusy(false);
      setShowToast(true);
      setToastProduct(addedLineItem.node);

      if (isPixelSupported(PIXELS.klaviyo)) {
        const SHOPIFY_PRODUCT_PREFIX = 'gid://shopify/Product/';
        const SHOPIFY_VARIANT_PREFIX = 'gid://shopify/ProductVariant/';

        //! some sites pass sanity "Product" (product.shopifyProduct)
        //! SportDOG passing shopify product only
        const klaviyoItem = {
          ProductName: product?.shopifyProduct?.title || product?.title,
          ProductID:
            product?.shopifyProduct?.shopifyId?.replace(
              SHOPIFY_PRODUCT_PREFIX,
              ''
            ) ?? product?.shopifyId?.replace(SHOPIFY_PRODUCT_PREFIX, ''),
          VariantID: activeVariant?.shopifyId?.replace(
            SHOPIFY_VARIANT_PREFIX,
            ''
          ),
          shopifyId: product?.shopifyProduct?.shopifyId || product?.shopifyId,
          SKU: activeVariant?.sku,
          ImageURL:
            activeVariant?.media?.[0]?.image?.src ||
            product?.store?.previewImageUrl,
          Brand: product?.shopifyProduct?.vendor || product?.vendor,
          Price: activeVariant?.price,
          CompareAtPrice: activeVariant?.compareAtPrice,
          URL: window.location.href,
        };

        const latestCart = newCartAddLine.cartLinesAdd.cart;

        const newItem = {
          $value: latestCart.cost.subtotalAmount.amount,
          AddedItemProductName: klaviyoItem.ProductName,
          AddedItemProductID: klaviyoItem.ProductID,
          AddedItemSKU: klaviyoItem.SKU,
          AddedItemCategories: [],
          AddedItemImageURL: klaviyoItem.ImageURL,
          AddedItemURL: window.location.href,
          AddedItemPrice: klaviyoItem.Price,
          AddedItemQuantity: quantity,
          ItemNames: latestCart.lines.edges.map(
            ({ node }) => node.merchandise.product.title
          ),
          CheckoutURL: latestCart.checkoutUrl,
          Items: latestCart.lines.edges.map(({ node }) => ({
            ProductID: node.merchandise.product.id.replace(
              SHOPIFY_PRODUCT_PREFIX,
              ''
            ),
            VariantID: node.merchandise.id?.replace(SHOPIFY_VARIANT_PREFIX, ''),
            SKU: node.merchandise.sku,
            ProductName: node.merchandise.product.title,
            Quantity: node.quantity,
            ItemPrice: node.merchandise.price.amount,
            CompareAtPrice: node.merchandise?.compareAtPrice?.amount,
            Brand: node.merchandise.product.vendor,
            shopifyId: node.merchandise.product.id,
            RowTotal: node.merchandise.price.amount * node.quantity,
            ImageURL: node.merchandise.image.url,
            ProductCategories: [],
          })),
        };

        waitForKlaviyo(() => {
          window?.klaviyo?.track('Added to cart', newItem);
        }, 2000);
      }

      if (isPixelSupported(PIXELS.adroll)) {
        // ! Adroll AddToCart Audience segment, different from event below for conversions
        // ! PetSafe only? or change with different account?
        window?.adroll?.track('pageView', { segment_name: 'e7d960f1' });

        const mappedAddonAdrollProducts =
          addOns?.map(s => ({
            product_id: s.sku,
            quantity: s.cartProps.quantity,
            category: pixelCategoryFromLocation(window?.location),
          })) || [];

        window?.adroll?.track('addToCart', {
          currency,
          products: [
            {
              product_id: activeVariant?.sku,
              quantity,
              price: activeVariant?.price,
              category: pixelCategoryFromLocation(window?.location),
            },
            ...mappedAddonAdrollProducts,
          ],
        });
      }
    } catch (error) {
      console.error('addToCart had error:', error);
      setCartBusy(false);
    }
  };

  const findLineItem = (lineItemId: string) =>
    cart.lines.edges.find(({ node }) => node.id === lineItemId);

  const incrementLineItem = async (lineItemId: string) => {
    const lineItem = findLineItem(lineItemId);

    if (lineItem) {
      updateLineItems([
        { id: lineItem.node.id, quantity: lineItem.node.quantity + 1 },
      ]);

      const lineItemPrice = lineItem?.node?.merchandise?.price?.amount ?? 0;

      if (isPixelSupported(PIXELS.gtm)) {
        pushToDataLayer('add_to_cart', {
          ecommerce: {
            currency,
            value: Number(lineItemPrice).toFixed(2),
          },
          items: [
            {
              variant: lineItem?.node?.merchandise,
              product: lineItem?.node?.merchandise?.product,
              quantity: 1,
            },
          ],
        });
      }
    }
  };

  /**
   * Decrease qty of lineItem. Will remove lineItem if qty is 0
   */
  const decrementLineItem = async (lineItemId: string) => {
    const lineItem = findLineItem(lineItemId);

    if (lineItem) {
      const removedLinePrice = lineItem?.node?.merchandise?.price?.amount ?? 0;

      if (isPixelSupported(PIXELS.gtm)) {
        pushToDataLayer('remove_from_cart', {
          ecommerce: {
            currency,
            value: Number(removedLinePrice).toFixed(2),
          },
          items: [
            {
              variant: lineItem?.node?.merchandise,
              product: lineItem?.node?.merchandise?.product,
              quantity: 1,
            },
          ],
        });
      }

      updateLineItems([
        { id: lineItem.node.id, quantity: lineItem.node.quantity - 1 },
      ]);
    }
  };

  const emptyCart = async () => {
    setCartBusy(true);

    try {
      const allLineItemIds = cart.lines.edges.map(({ node }) => node.id);

      const newCartRemoveLine = await shopifyClient.request(
        REMOVE_LINE_ITEM_QUERY,
        {
          cartId: cart.id,
          lineIds: allLineItemIds,
        }
      );

      await updateCart(newCartRemoveLine.cartLinesRemove.cart);
      setCart(newCartRemoveLine.cartLinesRemove.cart);
      setCartBusy(false);
    } catch (error) {
      console.error('emptyCart error:', error);
      setCartBusy(false);
    }
  };

  const deleteLineItem = async (lineItemId: string) => {
    setCartBusy(true);

    try {
      const newCartRemoveLine = await shopifyClient.request(
        REMOVE_LINE_ITEM_QUERY,
        {
          cartId: cart.id,
          lineIds: [lineItemId],
        }
      );

      const lineItem = findLineItem(lineItemId);
      const removedLinePrice = lineItem?.node?.merchandise?.price?.amount ?? 0;
      const removedLineQuantity = lineItem?.node?.quantity ?? 0;
      const totalPrice = removedLinePrice * removedLineQuantity;

      if (isPixelSupported(PIXELS.gtm)) {
        pushToDataLayer('remove_from_cart', {
          ecommerce: {
            currency,
            value: totalPrice.toFixed(2),
          },
          items: [
            {
              variant: lineItem?.node?.merchandise,
              product: lineItem?.node?.merchandise?.product,
              quantity: removedLineQuantity,
            },
          ],
        });
      }

      await updateCart(newCartRemoveLine.cartLinesRemove.cart);
      setCart(newCartRemoveLine.cartLinesRemove.cart);

      setCartBusy(false);
    } catch (error) {
      console.error('removeLineItem error:', error);
      setCartBusy(false);
    }
  };

  const hideToastNotification = () => {
    setShowToast(false);
    setToastProduct(null);
  };

  const writePepperjamData = () => {
    if (readBrowserStorage('pjnClickData')) {
      const pepperjamData = readBrowserStorage('pjnClickData');

      // duplicate pepperjam cookie and set domain where it can cross subdomains
      writeBrowserStorage('pjn-click', pepperjamData, getCookieDomain());
    }
  };

  const updateShopifyClient = () => {
    const client = new GraphQLClient(
      `https://${shopifySettingsForCurrency.domainName}/api/${CART_API_VERSION}/graphql.json`,
      {
        headers: {
          'X-Shopify-Storefront-Access-Token':
            shopifySettingsForCurrency.accessToken,
        },
      }
    );

    setShopifyClient(client);
    return client;
  };

  const updateCartAttributes = async (
    attributes: Array<{ key: string; value: string }>
  ) => {
    if (attributes) {
      try {
        const cartId = readBrowserStorage(shopifySettingsForCurrency.cartIdKey);

        const updatedCart = await shopifyClient.request(
          UPDATE_CART_ATTRIBUTES_QUERY,
          {
            cartId,
            attributes,
          }
        );

        if (updatedCart?.cartAttributesUpdate?.cart) {
          setCart(updatedCart.cartAttributesUpdate.cart);
        }
      } catch (err) {
        console.error('updateCartAttributes error:', err);
      }
      setCartBusy(false);
    }
  };

  // Need to keep this method for backwards compatibility on ifb site
  const formatPrice = (price: number) =>
    currency === 'CAD' ? formatCadPrice(price) : localizePrice(price);

  useEffect(() => {
    (async () => {
      try {
        await retry(
          async () => {
            const client = updateShopifyClient();
            const initCart = await initializeCart(
              client,
              shopifySettingsForCurrency
            );

            if (!initCart) setCart(defaultState.cart);
            await updateCart(initCart);
            writePepperjamData();
          },
          {
            maxTimeout: 1000,
            retries: 3,
          }
        );
      } catch (error) {
        console.error('Cart could not be created.', error);
        setCartError(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, shopifySettingsForCurrency]);

  const value = useMemo(
    () => ({
      cart,
      cartActive,
      cartBusy,
      cartError,
      deleteLineItem,
      addToCart,
      emptyCart,
      updateLineItems,
      incrementLineItem,
      decrementLineItem,
      setCartActive: setCartActiveHandler,
      showToast,
      hideToast: hideToastNotification,
      hideToastNotification,
      toastProduct,
      toastAddOns,
      updateCartAttributes,
      shopifyClient,
      formatPrice,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cart, cartActive, cartBusy, cartError, showToast, shopifyClient]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export { CartContext, CartProvider };
